<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header" :class="{ 'page-header--scrolled': headerScrolled }">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="openMenu">
            <ion-icon :icon="menuOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Полезные ссылки</ion-title>
        <ion-buttons slot="end">
          <HeaderMessengerButton />
          <ion-button v-if="isAdmin" @click="$router.push('/admin-useful-links')">
            <ion-icon :icon="createOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background events-content" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-refresher slot="fixed" @ionRefresh="load($event)">
        <ion-refresher-content></ion-refresher-content>
      </ion-refresher>
      <div class="ion-padding">
        <p v-if="loading" class="ion-text-center">Загрузка...</p>
        <p v-else-if="links.length === 0" class="ion-text-center">Нет доступных ссылок.</p>
        <ion-list v-else lines="full">
          <ion-item
            v-for="item in links"
            :key="item.id"
            class="link-item"
            button
            :detail="true"
            :class="{ 'link-item--opening': openingId === item.id }"
            @click="openExternal(item.id)"
          >
            <ion-label>
              <h2>{{ item.title }}</h2>
              <p v-if="openingId === item.id" class="link-opening-hint">Открываем…</p>
            </ion-label>
          </ion-item>
        </ion-list>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar
} from '@ionic/vue';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { menuController, toastController } from '@ionic/vue';
import { createOutline, menuOutline } from 'ionicons/icons';
import { defineComponent, inject } from 'vue';
import usefulLinksService, { IUsefulLinkMyItem } from '@/services/useful-links.service';

import HeaderMessengerButton from '@/components/HeaderMessengerButton.vue';

export default defineComponent({
  name: 'UsefulLinks',
  setup() {
    const accountService = inject<{ hasAnyAuthorityAndCheckAuth: (auth: string) => Promise<boolean> }>('accountService');
    return { accountService };
  },
  components: {
    HeaderMessengerButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonRefresher,
    IonRefresherContent,
    IonTitle,
    IonToolbar
  },
  data() {
    return {
      links: [] as IUsefulLinkMyItem[],
      loading: true,
      isAdmin: false,
      headerScrolled: false,
      openingId: null as number | null,
      menuOutline,
      createOutline
    };
  },
  async mounted() {
    this.isAdmin =
      (await this.accountService?.hasAnyAuthorityAndCheckAuth?.('ROLE_ADMIN')) ||
      (await this.accountService?.hasAnyAuthorityAndCheckAuth?.('ROLE_MANAGER')) ||
      false;
    await this.load();
  },
  ionViewWillEnter() {
    this.load();
  },
  methods: {
    openMenu() {
      menuController.open('mainMenu');
    },
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    async load(event?: CustomEvent) {
      this.loading = true;
      try {
        this.links = await usefulLinksService.getMyLinks();
      } catch {
        this.links = [];
      }
      this.loading = false;
      (event?.target as { complete?: () => void })?.complete?.();
    },
    async openExternal(id: number) {
      if (this.openingId !== null) return;
      this.openingId = id;
      /** Popup-блокер режет window.open после await — вкладка сразу при клике. */
      let placeholderTab: Window | null = null;
      if (!Capacitor.isNativePlatform()) {
        try {
          placeholderTab = window.open('about:blank', '_blank');
        } catch {
          placeholderTab = null;
        }
      }
      try {
        const { url } = await usefulLinksService.openLink(id);
        if (!url || typeof url !== 'string') {
          throw new Error('Сервер не вернул ссылку');
        }
        if (Capacitor.isNativePlatform()) {
          await Browser.open({ url });
        } else if (placeholderTab && !placeholderTab.closed) {
          placeholderTab.location.href = url;
        } else {
          window.location.href = url;
        }
      } catch (e) {
        console.error(e);
        try {
          if (placeholderTab && !placeholderTab.closed) placeholderTab.close();
        } catch {
          /* ignore */
        }
        const t = await toastController.create({
          message: 'Не удалось открыть ссылку. Проверьте сеть или попробуйте позже.',
          duration: 3500,
          color: 'danger',
          position: 'bottom'
        });
        await t.present();
      } finally {
        this.openingId = null;
      }
    }
  }
});
</script>

<style scoped>
.link-item {
  --background: var(--liga-surface);
  --color: var(--liga-chrome-text);
  --border-color: rgba(255, 255, 255, 0.1);
  --detail-icon-color: var(--liga-chrome-text);
  --detail-icon-opacity: 1;
}
.link-item--opening {
  opacity: 0.85;
  pointer-events: none;
}
ion-item h2 {
  color: var(--liga-chrome-text);
}
.link-opening-hint {
  color: var(--liga-chrome-text-muted);
  font-size: 0.85rem;
  margin-top: 4px;
}
</style>
